// src/Studio/timeline/thumbWorkerClient.ts
// Main-thread client for the thumbnail Web Worker.
// Follows the singleton pattern from ExportWorkerClient.

type PendingJob = {
  resolve: (dataUrl: string) => void;
  reject: (err: Error) => void;
};

export class ThumbWorkerClient {
  private worker: Worker | null = null;
  private pending = new Map<string, PendingJob>();
  private jobCounter = 0;

  initialize(): void {
    if (this.worker) return;
    this.worker = new Worker(new URL('./thumbWorker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (e: MessageEvent) => {
      const { type, jobId, dataUrl, error } = e.data;
      const job = this.pending.get(jobId);
      if (!job) return;
      this.pending.delete(jobId);
      if (type === 'result') {
        job.resolve(dataUrl);
      } else {
        job.reject(new Error(error || 'Thumb worker error'));
      }
    };
    this.worker.onerror = (e) => {
      // Reject all pending jobs on unrecoverable error
      for (const [id, job] of this.pending) {
        job.reject(new Error(`Worker error: ${e.message}`));
        this.pending.delete(id);
      }
    };
  }

  /**
   * Render a video frame bitmap to a JPEG data URL in the worker.
   * The bitmap is transferred (zero-copy) and closed by the worker.
   */
  async renderThumb(bitmap: ImageBitmap, frameW: number, frameH: number): Promise<string> {
    if (!this.worker) throw new Error('ThumbWorker not initialized');
    const jobId = `thumb-${this.jobCounter++}`;
    return new Promise<string>((resolve, reject) => {
      this.pending.set(jobId, { resolve, reject });
      this.worker!.postMessage(
        { type: 'render', jobId, bitmap, frameW, frameH },
        [bitmap], // Transfer the bitmap (zero-copy)
      );
    });
  }

  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    for (const [, job] of this.pending) {
      job.reject(new Error('Worker terminated'));
    }
    this.pending.clear();
  }
}

// Singleton
let instance: ThumbWorkerClient | null = null;

export function getThumbWorkerClient(): ThumbWorkerClient {
  if (!instance) {
    instance = new ThumbWorkerClient();
    instance.initialize();
  }
  return instance;
}

export function terminateThumbWorker(): void {
  if (instance) {
    instance.terminate();
    instance = null;
  }
}

// ---------------------------------------------------------------------------
// WebCodecs extraction worker (decodes frames directly from MP4 container)
// ---------------------------------------------------------------------------

type FrameCallback = (timestampMs: number, bitmap: ImageBitmap) => void;
type DoneCallback = (id: string) => void;
type ErrorCallback = (id: string, message: string) => void;

let extractWorker: Worker | null = null;
let extractWorkerFailed = false;
const pendingExtracts = new Map<string, {
  onFrame: FrameCallback;
  onDone: DoneCallback;
  onError: ErrorCallback;
}>();

function getExtractWorker(): Worker | null {
  if (extractWorkerFailed) return null;
  if (extractWorker) return extractWorker;
  try {
    extractWorker = new Worker(
      new URL('./thumbExtractWorker.ts', import.meta.url),
      { type: 'module' },
    );
    extractWorker.onmessage = (event: MessageEvent) => {
      const data = event.data;
      const req = pendingExtracts.get(data.id);
      if (!req) return;
      if (data.type === 'frame') {
        req.onFrame(data.timestampMs, data.bitmap);
      } else if (data.type === 'done') {
        pendingExtracts.delete(data.id);
        req.onDone(data.id);
      } else if (data.type === 'error') {
        pendingExtracts.delete(data.id);
        req.onError(data.id, data.message);
      }
    };
    extractWorker.onerror = () => {
      extractWorkerFailed = true;
      extractWorker = null;
    };
    return extractWorker;
  } catch {
    extractWorkerFailed = true;
    return null;
  }
}

let extractRequestCounter = 0;

export function extractFramesViaWorker(
  buffer: ArrayBuffer,
  timestamps: number[],
  maxWidth: number,
  maxHeight: number,
  onFrame: FrameCallback,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const w = getExtractWorker();
    if (!w) {
      reject(new Error('Extract worker unavailable'));
      return;
    }

    const id = `extract-${++extractRequestCounter}`;

    if (signal?.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }

    pendingExtracts.set(id, {
      onFrame,
      onDone: () => resolve(),
      onError: (_id, msg) => reject(new Error(msg)),
    });

    const onAbort = () => {
      pendingExtracts.delete(id);
      reject(new DOMException('aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });

    w.postMessage(
      { type: 'extract', id, buffer, timestamps, maxWidth, maxHeight },
      [buffer],
    );
  });
}

export function isThumbWorkerAvailable(): boolean {
  return (
    !extractWorkerFailed &&
    typeof Worker !== 'undefined' &&
    typeof VideoDecoder !== 'undefined'
  );
}
